import i18next, { type i18n as I18n } from 'i18next';
import { initReactI18next, useTranslation } from 'react-i18next';
import { DEFAULT_LANGUAGE, initialLanguage, writeLanguageChoice, type Language } from './language';
import { formatBytes } from '../format/bytes';
import { resources } from './resources';

/**
 * Инициализация i18next (§11, фаза 10, задача 1).
 *
 * Словари статические и лежат в бандле, а не подгружаются по сети: вместе
 * они весят десятки килобайт против сотен у одной только карты, зато
 * переключение языка происходит мгновенно и без сетевого запроса —
 * а §11 требует, чтобы переключение не требовало перезагрузки страницы.
 *
 * Готовый определитель языка (`i18next-browser-languagedetector`) не берётся
 * намеренно и в зависимостях не значится: правило выбора у нас своё и в три
 * строки (`language.ts`), а плагин пришлось бы настраивать под тот же
 * результат и держать в бандле.
 */

/** Возврат к русскому — правило §11: русский язык по умолчанию. */
const FALLBACK: Language = DEFAULT_LANGUAGE;

/**
 * Отдельный экземпляр словаря.
 *
 * `asDefault` регистрирует его в `react-i18next` как тот, которым пользуются
 * компоненты без `I18nextProvider`. Ставится ровно один раз — у общего
 * экземпляра приложения: `initReactI18next` перетирает эту регистрацию
 * каждым вызовом, и экземпляр, поднятый проверкой, иначе становился бы
 * языком всего приложения — включая проверки, которые его не поднимали.
 */
export function createI18n(
  language: Language = initialLanguage(),
  { asDefault = false }: { asDefault?: boolean } = {},
): I18n {
  const instance = i18next.createInstance();

  if (asDefault) {
    instance.use(initReactI18next);
  }

  void instance.init({
    resources,
    lng: language,
    fallbackLng: FALLBACK,
    defaultNS: 'translation',
    /*
      Разделитель пространств имён выключен: пространство одно, а двоеточие
      в ключах мешает — оно встречается в подписях («Порт: 22») и в кодах
      прав (`node:read`), которые ходят в подстановках.
    */
    nsSeparator: false,
    interpolation: {
      // React экранирует сам; повторное экранирование превратило бы
      // кавычки-ёлочки и тире в мусор вида `&#171;`.
      escapeValue: false,
    },
  });

  /*
    Именованный формат для подстановок в словарях (§11, фаза 10, задача 7).

    Объём данных подстановкой: `{{size, bytes}}`.

    Нужен там, где число байт приходит с сервера, — предел на передачу файла,
    размер отвергнутого файла (ADR 028: сервер шлёт число, а не фразу).
    Единицу берёт из словаря та же `formatBytes`, что и остальной интерфейс:
    двух разных «МБ» на одном экране быть не должно.
  */
  instance.services.formatter?.add('bytes', (value: unknown) =>
    formatBytes(Number(value), instance.t.bind(instance)),
  );

  return instance;
}

/** Общий экземпляр приложения. Проверки поднимают свой через `createI18n`. */
export const i18n = createI18n(initialLanguage(), { asDefault: true });

/**
 * Переключение языка.
 *
 * Выбор сохраняется здесь, а не в компоненте переключателя: язык меняют
 * и из шапки, и из ящика на телефоне, и забыть сохранение в одном
 * из двух мест — ровно тот дефект, который §11 запрещает («переживает
 * выход и повторный вход»).
 */
export async function changeLanguage(language: Language): Promise<void> {
  writeLanguageChoice(language);
  await i18n.changeLanguage(language);
  document.documentElement.lang = language;
}

/** Текущий язык интерфейса. */
export function currentLanguage(): Language {
  const active = i18n.resolvedLanguage ?? i18n.language;
  return active === 'en' ? 'en' : 'ru';
}

export { useTranslation };
export type { Language };
export { LANGUAGES, LANGUAGE_NAMES, DEFAULT_LANGUAGE } from './language';
